import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Formik, Form, Field } from "formik"
import { generateValidation } from "./validations"
import ReCaptcha from "./ReCaptcha"
import {
  verifyForm,
  contactFormNotificationAPI,
} from "@/services/formSubmitAPI/client"

const captchaDisabled = process.env.NEXT_PUBLIC_RECAPTCHA_DISABLE === "0"

const captchaField = {
  name: "captchaToken",
  type: "captcha",
  required: true,
  message: "Please verify the captcha",
}

const optionalFieldNames = new Set(["first_name", "last_name", "phone"])

// Returns red border class if field has error, normal border otherwise
function inputClass(name, errors, touched) {
  const base = "w-full border rounded px-3 py-2 text-[15px]"
  return errors[name] && touched[name]
    ? `${base} border-red-500`
    : `${base} border-slate-300`
}

// Returns aria-describedby id only when field has a visible error
function ariaDescribedBy(name, errors, touched) {
  return errors[name] && touched[name] ? `${name}-error` : undefined
}

export default function ContactUsForm({
  formConfig = {},
  contactFormConfigurationsData = {},
}) {
  const [success, setSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const contactFormConfigurations =
    contactFormConfigurationsData.items?.[0]?.fields?.configurations.filter(
      config => config?.fields?.internalTitle === "Indonesia - Contact Us Form"
    ) || {}
  const filteredContactFormConfigurations =
    contactFormConfigurations?.[0]?.fields?.config

  const {
    formFields = [],
    thankYouMessage = "",
    heading = "",
    url = "",
    privacyPolicy = "",
  } = formConfig

  const normalizedFormFields = React.useMemo(
    () =>
      formFields.map(field =>
        optionalFieldNames.has(field.name)
          ? { ...field, required: false }
          : field
      ),
    [formFields]
  )

  // One empty string per field as starting value
  const initialValues = React.useMemo(
    () =>
      normalizedFormFields.reduce(
        (acc, field) => ({ ...acc, [field.name]: "" }),
        {
          captchaToken: "",
        }
      ),
    [normalizedFormFields]
  )

  // Captcha validation only needed in production
  const validationSchema = generateValidation([
    ...normalizedFormFields,
    ...(captchaDisabled ? [] : [captchaField]),
  ])

  // Load Google reCAPTCHA script (production only)
  // useEffect(() => {
  //   if (captchaDisabled) return

  //   const script = document.createElement("script")
  //   script.src = "https://www.google.com/recaptcha/api.js"
  //   script.async = true
  //   document?.body?.appendChild(script)

  //   return () => document?.body?.removeChild(script)
  // }, [captchaDisabled])

  async function handleSubmit(
    values,
    { setSubmitting, setFieldError, resetForm }
  ) {
    setSubmitError(null)

    try {
      // In dev: skip captcha, submit directly
      if (captchaDisabled) {
        const res = await contactFormNotificationAPI({
          values,
          filteredContactFormConfigurations,
        })
        if (res?.message) {
          resetForm()
          setSuccess(true)
        }
      }

      // Step 1: Check captcha token exists
      if (!values.captchaToken) {
        setFieldError("captchaToken", "Please verify the captcha")
        setSubmitting(false)
        return
      }

      // Step 2: Verify captcha with server
      const verifyData = await verifyForm(values.captchaToken)

      if (verifyData && verifyData?.data?.success) {
        // Step 3: Send only known field names (exclude captcha etc.)
        const fieldNames = new Set(normalizedFormFields.map(f => f.name))
        const payload = Object.fromEntries(
          Object.entries(values).filter(([key]) => fieldNames.has(key))
        )
        const res = await contactFormNotificationAPI({
          values: payload,
          filteredContactFormConfigurations,
        })
        if (res?.message) {
          resetForm()
          setSuccess(true)
        }
      } else {
        setFieldError("captchaToken", "Captcha verification failed")
        setSubmitting(false)
        return
      }
    } catch (err) {
      console.error(err)
      setSubmitError("Something went wrong. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  const handleCloseModal = () => {
    setSuccess(false)
    window.scrollTo({
      top: 0,
      behavior: "smooth", // optional (remove if instant chahiye)
    })
  }

  const openPopup = (e, url) => {
    e.preventDefault()
    // Specifying width and height forces a popup window instead of a tab
    window.open(
      url,
      "popupWindow",
      "width=1000,height=600,scrollbars=yes,location=yes,status=yes"
    )
  }

  return (
    <div className="mx-auto max-w-[980px] px-4 py-[50px]">
      <h2 className="text-center text-[45px] md:text-[60px] font-[HelveticaLight,Helvetica,sans-serif] text-gray-800">
        {heading}
      </h2>
      <hr className="mt-3 mb-8 border-t border-black-300" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, submitCount, isSubmitting }) => (
          <Form className="space-y-5 w-3/4 mx-auto" noValidate>
            {/* ── Fields ── */}
            {normalizedFormFields.map(field => (
              <div
                key={field.name}
                className="grid gap-2 md:gap-4 items-start md:grid-cols-[220px_minmax(0,328px)]"
              >
                <label
                  htmlFor={field.name}
                  className="text-sm text-slate-700 md:text-right md:pt-2"
                >
                  {field.label}
                  {field.required && (
                    <span aria-hidden="true" className="ml-1 text-red-500">
                      *
                    </span>
                  )}
                </label>

                <div className="max-w-[328px] w-full">
                  {field.type === "select" && (
                    <Field
                      as="select"
                      id={field.name}
                      name={field.name}
                      className={inputClass(field.name, errors, touched)}
                      aria-invalid={Boolean(
                        errors[field.name] && touched[field.name]
                      )}
                      aria-describedby={ariaDescribedBy(
                        field.name,
                        errors,
                        touched
                      )}
                    >
                      <option value="">-- Select --</option>
                      {field.options?.map(opt => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Field>
                  )}

                  {(field.type === "text" || field.type === "email") && (
                    <Field
                      type={field.type}
                      id={field.name}
                      name={field.name}
                      className={inputClass(field.name, errors, touched)}
                      aria-invalid={Boolean(
                        errors[field.name] && touched[field.name]
                      )}
                      aria-describedby={ariaDescribedBy(
                        field.name,
                        errors,
                        touched
                      )}
                      onInput={
                        field.name === "first_name" ||
                        field.name === "last_name"
                          ? e => {
                              const value = e.target.value.replace(
                                /[^a-zA-Z]/g,
                                ""
                              )
                              e.target.value = value
                            }
                          : field.name === "phone"
                            ? e => {
                                const value = e.target.value.replace(/\D/g, "")
                                e.target.value = value
                              }
                            : undefined
                      }
                    />
                  )}

                  {field.type === "textarea" && (
                    <Field
                      as="textarea"
                      id={field.name}
                      name={field.name}
                      rows="5"
                      className={inputClass(field.name, errors, touched)}
                      aria-invalid={Boolean(
                        errors[field.name] && touched[field.name]
                      )}
                      aria-describedby={ariaDescribedBy(
                        field.name,
                        errors,
                        touched
                      )}
                    />
                  )}

                  {errors[field.name] && touched[field.name] && (
                    <p
                      id={`${field.name}-error`}
                      role="alert"
                      className="text-red-600 text-xs mt-1"
                    >
                      {errors[field.name]}
                    </p>
                  )}
                </div>
              </div>
            ))}

            {/* ── reCAPTCHA ── */}
            {!captchaDisabled && (
              <div className="grid md:grid-cols-[220px_minmax(0,420px)] md:gap-4">
                <div />
                <div className="max-w-[420px] w-full">
                  <div
                    className={
                      errors.captchaToken && submitCount > 0
                        ? "[&_iframe]:border-2 [&_iframe]:border-red-500 [&_iframe]:rounded"
                        : ""
                    }
                  >
                    <ReCaptcha
                      siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY}
                      onChange={token =>
                        setFieldValue("captchaToken", token || "")
                      }
                      onExpired={() => setFieldValue("captchaToken", "")}
                      onError={() => setFieldValue("captchaToken", "")}
                    />
                  </div>

                  {errors.captchaToken && submitCount > 0 && (
                    <p
                      id="captchaToken-error"
                      role="alert"
                      className="text-red-600 text-xs mt-1"
                    >
                      {errors.captchaToken}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Submit error ── */}
            {submitError && (
              <div
                role="alert"
                aria-live="assertive"
                className="text-red-600 text-sm text-center"
              >
                {submitError}
              </div>
            )}

            {/* ── Submit button ── */}
            <div className="grid md:grid-cols-[220px_minmax(0,328px)] md:gap-4 mt-2">
              <div />
              <div className="max-w-[328px] w-full">
                {url && privacyPolicy && (
                  <a
                    href={url}
                    rel="noopener noreferrer"
                    className="block mb-3 text-sm text-blue-600 underline"
                    onClick={e => openPopup(e, url)}
                  >
                    {privacyPolicy}
                  </a>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#3E4A75] text-white py-3 uppercase rounded disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting…" : "Submit"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      {success && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div
            className="bg-white rounded-lg p-8 max-w-md w-full text-center shadow-lg"
            role="alertdialog"
            aria-labelledby="thank-you-title"
          >
            <h3
              id="thank-you-title"
              className="text-2xl font-bold text-gray-800 mb-4"
            >
              Thank You
            </h3>
            <p className="text-gray-600 mb-6">{thankYouMessage}</p>
            <button
              onClick={handleCloseModal}
              className="bg-[#3E4A75] text-white px-6 py-2 rounded hover:bg-opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const FieldShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  type: PropTypes.oneOf(["text", "email", "textarea", "select"]).isRequired,
  required: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.string),
})

ContactUsForm.propTypes = {
  formConfig: PropTypes.shape({
    heading: PropTypes.string,
    url: PropTypes.string,
    privacyPolicy: PropTypes.string,
    formFields: PropTypes.arrayOf(FieldShape),
  }),
  contactFormConfigurationsData: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object),
  }),
}
